import React, { useCallback } from "react";
import { Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";

import ActivityFeed from "./ActivityFeed";
import AssigneePicker from "./AssigneePicker";
import AttachmentsPanel from "./AttachmentsPanel";
import CommentThread from "./CommentThread";
import ComponentPicker from "./ComponentPicker";
import DependencyPanel from "./DependencyPanel";
import EpicChildrenList from "./EpicChildrenList";
import EpicLink from "./EpicLink";
import IssueKeyBadge from "./IssueKeyBadge";
import LabelsPicker from "./LabelsPicker";
import LinksPanel from "./LinksPanel";
import PriorityPicker from "./PriorityPicker";
import ResolutionPicker from "./ResolutionPicker";
import StatusPicker from "./StatusPicker";
import SubtasksList from "./SubtasksList";
import VersionPicker from "./VersionPicker";
import WatcherMenu from "./WatcherMenu";
import WorklogPanel from "./WorklogPanel";
import UserAvatar from "../../components/UserAvatar";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { fetchIssueById, updateIssue } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectLabels,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import {
  ISSUE_TYPES,
  PRIORITY_OPTIONS,
} from "../../constants/projects.constants";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type {
  Issue,
  IssuePriority,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

interface IssueDetailLayoutProps {
  issue: Issue | null;
  loading: boolean;
  currentUserId: number | null;
}

const IssueDetailLayout: React.FC<IssueDetailLayoutProps> = ({
  issue,
  loading,
  currentUserId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const statuses = useSelector(selectStatuses);
  const labels = useSelector(selectLabels);
  const activeCompany = useSelector(selectActiveCompany);

  const canEditIssue = useProjectCapability(issue?.project_id, "CREATE_ISSUE");
  const canManageAttachment = useProjectCapability(issue?.project_id, "MANAGE_ATTACHMENT");
  const canManageWorklog = useProjectCapability(issue?.project_id, "MANAGE_WORKLOG");
  const canManageWatcher = useProjectCapability(issue?.project_id, "MANAGE_WATCHER");
  const canManageLink = useProjectCapability(issue?.project_id, "MANAGE_LINK");
  const canManageDependency = useProjectCapability(issue?.project_id, "MANAGE_DEPENDENCY");
  const canComment = useProjectCapability(issue?.project_id, "CREATE_ISSUE");

  const issueLabelIds = (issue?.labels || []).map((l) => l.id);

  const persistField = useCallback(
    async (payload: Record<string, any>) => {
      if (!issue || !canEditIssue) return;
      try {
        await dispatch(
          updateIssue({ issueId: issue.id, payload })
        ).unwrap();
        dispatch(fetchIssueById(issue.id));
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to update issue"));
      }
    },
    [dispatch, issue, canEditIssue]
  );

  const handleStatusChange = (statusId: number) =>
    persistField({ status_id: statusId });

  const handlePriorityChange = (priority: IssuePriority) =>
    persistField({ priority });

  const handleAssigneeChange = (
    userId: number | null,
    snapshot: Record<string, any> | null
  ) =>
    persistField({
      assignee_id: userId,
      assignee_snapshot: snapshot,
    });

  const handleLabelsChange = async (labelIds: number[]) => {
    if (!issue || !canEditIssue) return;
    try {
      await issuesApi.setIssueLabels(
        activeCompany.company_id,
        activeCompany.company_type,
        issue.id,
        labelIds
      );
      dispatch(fetchIssueById(issue.id));
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to update labels"));
    }
  };

  const handleEpicChange = (epicId: number | null) =>
    persistField({ epic_id: epicId });

  const handleComponentChange = (componentId: number | null) =>
    persistField({ component_id: componentId });

  const handleFixVersionChange = (versionId: number | null) =>
    persistField({ fix_version_id: versionId });

  const handleAffectsVersionChange = (versionId: number | null) =>
    persistField({ affects_version_id: versionId });

  const handleResolutionChange = (resolutionId: number | null) =>
    persistField({ resolution_id: resolutionId });

  if (loading || !issue) {
    return (
      <div className="text-center py-5">
        <Spin />
      </div>
    );
  }

  const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
  const priorityMeta = PRIORITY_OPTIONS.find(
    (p) => p.value === issue.priority
  );

  return (
    <div className="row g-3">
      <div className="col-lg-8">
        <div className="card mb-3">
          <div className="card-body bg-white rounded-3">
            <div className="d-flex flex-wrap align-items-center gap-2 text-muted small mb-2">
              <span>{issue.issue_key}</span>
              <span>·</span>
              <span
                className="d-inline-flex align-items-center gap-1"
                style={{ color: typeMeta?.color }}
              >
                <span
                  className="d-inline-block rounded-circle"
                  style={{
                    width: 8,
                    height: 8,
                    background: typeMeta?.color || "#6B7280",
                  }}
                />
                {typeMeta?.label || issue.type}
              </span>
              <span>·</span>
              <span>Created {humanReadableDate(issue.created_at)}</span>
              <div
                className="ms-auto"
                style={canManageWatcher ? undefined : { pointerEvents: "none", opacity: 0.5 }}
              >
                <WatcherMenu
                  issueId={issue.id}
                  currentUserId={currentUserId}
                  initialWatchers={issue.watchers || []}
                />
              </div>
            </div>
            <h3 className="mb-3">{issue.title}</h3>
            {issue.description ? (
              <div
                className="text-body"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {issue.description}
              </div>
            ) : (
              <p className="text-muted mb-0">No description provided.</p>
            )}
          </div>
        </div>

        {issue.type !== "SUBTASK" && (
          <div className="card mb-3">
            <div className="card-header border-0 pb-0">
              <h6 className="mb-0">
                {issue.type === "EPIC" ? "Issues in this epic" : "Subtasks"}
              </h6>
            </div>
            <div className="card-body bg-white rounded-3">
              {issue.type === "EPIC" ? (
                <EpicChildrenList epicId={issue.id} epicColor={issue.epic_color} />
              ) : (
                <SubtasksList
                  parentIssue={issue}
                  initialSubtasks={issue.subtasks || []}
                />
              )}
            </div>
          </div>
        )}

        <div className="card mb-3">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Time Tracking</h6>
          </div>
          <div
            className="card-body bg-white rounded-3"
            style={canManageWorklog ? undefined : { pointerEvents: "none", opacity: 0.6 }}
          >
            <WorklogPanel
              issueId={issue.id}
              initialWorklogs={issue.worklogs || []}
              estimateSeconds={issue.estimate_seconds}
              remainingSeconds={issue.remaining_seconds}
              currentUserId={currentUserId}
            />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Linked Issues</h6>
          </div>
          <div
            className="card-body bg-white rounded-3"
            style={canManageLink ? undefined : { pointerEvents: "none", opacity: 0.6 }}
          >
            <LinksPanel
              issueId={issue.id}
              projectId={issue.project_id}
              initialLinks={
                typeof issue.linked_issues === "object" && !Array.isArray(issue.linked_issues)
                  ? (issue.linked_issues || {})
                  : {}
              }
            />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Attachments</h6>
          </div>
          <div
            className="card-body bg-white rounded-3"
            style={canManageAttachment ? undefined : { pointerEvents: "none", opacity: 0.6 }}
          >
            <AttachmentsPanel
              entityType="ISSUE"
              entityId={issue.id}
              currentUserId={currentUserId}
            />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Comments</h6>
          </div>
          <div
            className="card-body bg-white rounded-3"
            style={canComment ? undefined : { pointerEvents: "none", opacity: 0.6 }}
          >
            <CommentThread issueId={issue.id} currentUserId={currentUserId} />
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="card mb-3">
          <div className="card-body bg-white rounded-3">
            <h6 className="mb-3">Details</h6>
            <div className="d-flex flex-column gap-3">
              <div>
                <label className="form-label small fw-semibold">Status</label>
                <StatusPicker
                  statuses={statuses}
                  value={issue.status_id}
                  onChange={handleStatusChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Priority</label>
                <PriorityPicker
                  value={issue.priority}
                  onChange={handlePriorityChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Assignee</label>
                <AssigneePicker
                  value={issue.assignee_id}
                  assigneeSnapshot={issue.assignee_snapshot}
                  onChange={handleAssigneeChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Reporter</label>
                <div className="d-flex align-items-center gap-2">
                  <UserAvatar
                    name={issue.reporter_snapshot?.name}
                    img={issue.reporter_snapshot?.img}
                    size={24}
                    fontSize={11}
                  />
                  <span className="small">
                    {issue.reporter_snapshot?.name ||
                      `User #${issue.reporter_id}`}
                  </span>
                </div>
              </div>
              <div>
                <label className="form-label small fw-semibold">Labels</label>
                <LabelsPicker
                  labels={labels}
                  value={issueLabelIds}
                  onChange={handleLabelsChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Due Date</label>
                <div className="small">
                  {issue.due_date
                    ? humanReadableDate(issue.due_date)
                    : "—"}
                </div>
              </div>
              <div>
                <label className="form-label small fw-semibold">
                  Story Points
                </label>
                <div className="small">{issue.story_points ?? "—"}</div>
              </div>
              {issue.type !== "EPIC" && issue.type !== "SUBTASK" && (
                <div>
                  <label className="form-label small fw-semibold">Epic</label>
                  <EpicLink
                    projectId={issue.project_id}
                    value={issue.epic_id}
                    onChange={handleEpicChange}
                    excludeId={issue.id}
                    size="small"
                    disabled={!canEditIssue}
                  />
                </div>
              )}
              <div>
                <label className="form-label small fw-semibold">Component</label>
                <ComponentPicker
                  value={issue.component_id}
                  onChange={handleComponentChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Fix Version</label>
                <VersionPicker
                  value={issue.fix_version_id}
                  onChange={handleFixVersionChange}
                  size="small"
                  label="Fix version"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Affects Version</label>
                <VersionPicker
                  value={issue.affects_version_id}
                  onChange={handleAffectsVersionChange}
                  size="small"
                  label="Affects version"
                  disabled={!canEditIssue}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold">Resolution</label>
                <ResolutionPicker
                  value={issue.resolution_id}
                  onChange={handleResolutionChange}
                  size="small"
                  disabled={!canEditIssue}
                />
              </div>
              {issue.parent_issue && (
                <div>
                  <label className="form-label small fw-semibold">Parent</label>
                  <div className="small">
                    <IssueKeyBadge
                      projectKey={issue.parent_issue.issue_key.split("-")[0]}
                      issueKey={issue.parent_issue.issue_key}
                    />
                    <span className="ms-2">{issue.parent_issue.title}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Dependencies</h6>
          </div>
          <div
            className="card-body bg-white rounded-3 pt-2"
            style={canManageDependency ? undefined : { pointerEvents: "none", opacity: 0.6 }}
          >
            <DependencyPanel
              issueId={issue.id}
              projectId={issue.project_id}
            />
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-header border-0 pb-0">
            <h6 className="mb-0">Activity</h6>
          </div>
          <div className="card-body bg-white rounded-3">
            <ActivityFeed
              activity={issue.activity || []}
              initialLimit={10}
              viewAll={{
                projectId: issue.project_id,
                entityType: "ISSUE",
                entityId: issue.id,
                title: `Activity — ${issue.issue_key}`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailLayout;
